import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { JSONContent } from "@tiptap/react";
import { ControlledAutocomplete } from "../../../components/forms/AutocompleteSearch";
import { ButtonGroup } from "../../../components/forms/ButtonGroup";
import { MentionEditor } from "./mention-editor";
import ConditionsEditor, { EditableCondition } from "./ConditionsEditor";
import TemplateColumnsEditor from "./TemplateColumnsEditor";
import { useAssemblyContext } from "../context/AssemblyContext";
import { useGetGroupsQuery, useGetGroupPromptsQuery } from "../api/playbookApi";
import { FlowGroupType, GroupModel } from "../types/group-model";
import { PromptModel, PromptDataType } from "../types/prompt-model";
import { AssemblyType } from "../types/assembly-model";
import {
  AssemblyReferenceModel,
  AssemblyReferenceWriteModel,
  EMultiDocumentListFormat,
  ESingleDocumentListFormat,
  ESingleDocumentReferenceFormat,
} from "../types/assembly-reference";
import { AssemblyReferenceType } from "../types/assembly-condition";
import { AssemblyReferenceTemplateColumnWrite, isTemplateEmpty } from "../types/assembly-template";
import { collectPromptObjectIdsFromTemplate, PromptMentionSource } from "./mention-editor/types";
import { GetFilterDataTypeString } from "../utils/formatters";

type ReferenceMode = "list-as" | "per-document";

export interface ReferenceBuilderProps {
  assemblyType: AssemblyType;
  /**
   * Group a per-document assembly belongs to. When set, the group picker is
   * locked to it and only its prompts are referenceable. Ignored for flow-wide.
   */
  assignedGroupObjectIdentifier?: string;
  onSubmit: (model: AssemblyReferenceWriteModel, promptLabel: string) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
  loading: boolean;
  error: boolean;
  existingReference?: AssemblyReferenceModel;
  isEditMode?: boolean;
}

const ReferenceBuilder = ({
  assemblyType,
  assignedGroupObjectIdentifier,
  onSubmit,
  onDelete,
  onClose,
  loading,
  error,
  existingReference,
  isEditMode = false,
}: ReferenceBuilderProps) => {
  const ctx = useAssemblyContext();
  const supportsModeChoice = assemblyType === AssemblyType.DocxFlowWide;

  // Per-document assemblies are pinned to their assigned group: the picker is
  // hidden and prompts come from that group only. Flow-wide assemblies let the
  // user pick any group first.
  const lockedGroupObjectIdentifier = !supportsModeChoice ? assignedGroupObjectIdentifier : undefined;

  // Prompt selection (add mode only — editing keeps the existing prompt).
  const [selectedGroup, setSelectedGroup] = useState<GroupModel | null>(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<PromptModel | null>(null);
  const [promptSearch, setPromptSearch] = useState("");

  // Configuration state.
  const [multiFormat, setMultiFormat] = useState<EMultiDocumentListFormat | undefined>(
    existingReference?.multiDocumentListFormat
  );
  const [singleFormat, setSingleFormat] = useState<ESingleDocumentReferenceFormat | undefined>(
    existingReference?.singleDocumentReferenceFormat
  );
  const [answerListFormat, setAnswerListFormat] = useState<ESingleDocumentListFormat | undefined>(
    existingReference?.singleDocumentAnswerListFormat
  );
  const [template, setTemplate] = useState<JSONContent | null>(
    existingReference?.template ?? null
  );
  const [templateColumns, setTemplateColumns] = useState<AssemblyReferenceTemplateColumnWrite[]>(
    existingReference?.templateColumns.map((c) => ({ name: c.name, template: c.template })) ?? []
  );
  const [conditions, setConditions] = useState<EditableCondition[]>(
    existingReference?.conditions.map((c) => ({
      isYes: c.isYes,
      fuzzyMatch: c.fuzzyMatch,
      minimumDecimalAnswer: c.minimumDecimalAnswer,
      maximumDecimalAnswer: c.maximumDecimalAnswer,
      textAnswer: c.textAnswer,
      allCategoriesMatch: c.allCategoriesMatch,
      decimalAnswer: c.decimalAnswer,
      decimalOperator: c.decimalOperator,
      dateOperator: c.dateOperator,
      startDate: c.startDate,
      endDate: c.endDate,
      dateAnswer: c.dateAnswer,
      // Client-only stable key for the editor list; reuse the existing
      // condition's server id so it stays stable across edits. Never sent.
      _key: c.objectIdentifier,
      targetPromptObjectIdentifier: c.targetPromptObjectIdentifier,
      categoryConditions: c.categoryConditions.map((cc) => ({
        text: cc.text,
        fuzzyMatch: cc.fuzzyMatch,
      })),
    })) ?? []
  );
  const [submitted, setSubmitted] = useState(false);

  // Single-prompt (old) vs multi-prompt (new) List As authoring. Single: one
  // prompt picked from a dropdown, its fields inserted with the legacy "@"
  // mention. Multi: prompts referenced inline with the "@"/"." cascade. Defaults
  // to the old experience; when editing, derive it from the stored reference so
  // the right editor opens (multi if it has "@" mentions or more than one prompt).
  const [singlePrompt, setSinglePrompt] = useState<boolean>(() => {
    if (!isEditMode || !existingReference) return true;
    const templates = [
      existingReference.template,
      ...existingReference.templateColumns.map((c) => c.template),
    ];
    const usesPromptMentions = templates.some(
      (t) => collectPromptObjectIdsFromTemplate(t).length > 0
    );
    return !(usesPromptMentions || existingReference.prompts.length > 1);
  });

  const { data: groups = [], isLoading: groupsLoading } = useGetGroupsQuery(ctx);

  // When locked (per-document), prompts come from the assigned group; when
  // editing, from the group the reference's prompt belongs to (there is no
  // group picker in edit mode); otherwise from whichever group the user picked.
  const effectiveGroupObjectIdentifier =
    lockedGroupObjectIdentifier ??
    (isEditMode ? existingReference?.groupObjectIdentifier : undefined) ??
    selectedGroup?.objectIdentifier ??
    "";

  const { data: prompts = [], isLoading: promptsLoading } = useGetGroupPromptsQuery(
    {
      ctx,
      groupObjectIdentifier: effectiveGroupObjectIdentifier,
      searchText: promptSearch,
    },
    { skip: !effectiveGroupObjectIdentifier }
  );

  // The conditions editor offers every prompt in the selected group as a
  // possible target, so it needs the full, unfiltered list — independent of
  // whatever the user has typed into the prompt-selection search box above.
  const { data: groupPrompts = [] } = useGetGroupPromptsQuery(
    {
      ctx,
      groupObjectIdentifier: effectiveGroupObjectIdentifier,
      searchText: "",
    },
    { skip: !effectiveGroupObjectIdentifier }
  );

  // Every prompt in the group is referenceable from a List As template via
  // "@" — there is no separate prompt picker; the reference draws on whichever
  // prompts the author actually mentions. Typing "." after a "@" prompt offers
  // that prompt's fields, branching on its type.
  const promptMentionSources: PromptMentionSource[] = useMemo(
    () =>
      groupPrompts.map((p) => ({
        objectIdentifier: p.objectIdentifier,
        label: p.label,
        promptDataType: p.promptDataTypeId,
      })),
    [groupPrompts]
  );

  const promptDataType: PromptDataType | undefined =
    selectedPrompt?.promptDataTypeId ?? existingReference?.filter?.promptDataTypeId;

  const promptLabel = selectedPrompt?.label ?? existingReference?.promptLabel ?? "";
  // The reference mode is driven entirely by the group type — there is no
  // user choice. Bundle groups roll every document into a single answer
  // per collection, so "list as" (one row per document) is meaningless:
  // they get per-document only. Every other group type gets "list as" only.
  const isCombinedDocumentsGroup =
    selectedGroup?.flowGroupType === FlowGroupType.Bundle;
  const effectiveReferenceMode: ReferenceMode = (() => {
    if (!supportsModeChoice) return "per-document";
    // Editing keeps the mode the existing reference was saved with — there is
    // no group selection in edit mode to derive it from.
    if (isEditMode) {
      return existingReference?.multiDocumentListFormat ? "list-as" : "per-document";
    }
    return selectedGroup && !isCombinedDocumentsGroup ? "list-as" : "per-document";
  })();
  const isListAs = effectiveReferenceMode === "list-as";
  const isTable = isListAs && multiFormat === EMultiDocumentListFormat.Table;

  // A List As reference draws on exactly the prompts its template (or, for a
  // table, its column templates) mentions via "@". This is the source of truth
  // for which prompts the reference is linked to — there is no picker.
  const referencedPromptIds: string[] = useMemo(() => {
    const templates = isTable ? templateColumns.map((c) => c.template) : [template];
    const ids = templates.flatMap((t) => collectPromptObjectIdsFromTemplate(t));
    return [...new Set(ids)];
  }, [isTable, template, templateColumns]);

const validationError = (): string | null => {
  // A List As reference draws on the prompts the author selected (referenced
  // in its template/columns), so it needs a group (to offer those prompts) and
  // at least one prompt. A per-document reference targets a single prompt.
  if (!isEditMode && isListAs && !selectedGroup && !lockedGroupObjectIdentifier)
    return "Select a group.";
  if (isListAs && !multiFormat) return "Choose a reference format.";
  if (!isListAs && !singleFormat) return "Choose a reference format.";
  // Single-prompt List As needs the one prompt picked; multi-prompt needs at
  // least one "@" mention in the template. (Only enforced when adding — an
  // existing legacy reference has neither but still exports correctly.)
  if (!isEditMode && isListAs && singlePrompt && !selectedPrompt) return "Select a prompt.";
  if (!isEditMode && isListAs && !singlePrompt && referencedPromptIds.length === 0)
    return "Reference at least one prompt in the template using “@”.";
  if (!isEditMode && !isListAs && !selectedPrompt) return "Select a prompt.";

  const needsAnswerListFormat =
    !isListAs &&
    singleFormat === ESingleDocumentReferenceFormat.Answer &&
    promptDataType === PromptDataType.ListOfValues;
  if (needsAnswerListFormat && !answerListFormat) return "Choose an answer list format.";

  return null;
};

  const handleSubmit = async () => {
    setSubmitted(true);
    if (validationError()) return;

    // Which prompt(s) the reference draws on. Single-prompt List As uses the one
    // picked prompt; multi-prompt uses exactly the prompts its template/columns
    // mention via "@" (recomputed so edits stay in sync). Either way, on edit we
    // fall back to the reference's existing prompts when nothing new is
    // selected/mentioned. A per-document reference targets the single prompt.
    const existingPromptIds = isEditMode
      ? existingReference!.prompts.map((p) => p.objectIdentifier)
      : [];
    const listAsPromptIds = singlePrompt
      ? selectedPrompt
        ? [selectedPrompt.objectIdentifier]
        : existingPromptIds
      : referencedPromptIds.length > 0
        ? referencedPromptIds
        : existingPromptIds;
    const promptObjectIdentifiers = isListAs
      ? listAsPromptIds
      : isEditMode
        ? existingPromptIds
        : selectedPrompt
          ? [selectedPrompt.objectIdentifier]
          : [];

    const model: AssemblyReferenceWriteModel = {
      promptObjectIdentifiers,
      assemblyReferenceType:
        ctx.source === "flow"
          ? isListAs
            ? AssemblyReferenceType.FlowWide
            : AssemblyReferenceType.Document
          : undefined,
      multiDocumentListFormat: isListAs ? multiFormat : undefined,
      singleDocumentReferenceFormat: !isListAs ? singleFormat : undefined,
      singleDocumentAnswerListFormat: !isListAs ? answerListFormat : undefined,
      template: isListAs && !isTable && !isTemplateEmpty(template) ? template : null,
      templateColumns: isTable ? templateColumns : [],
      // Drop the client-only `_key` so it never reaches the server.
      conditions: conditions.map(({ _key, ...rest }) => rest),
    };

    // A List As reference isn't tied to one prompt, so label the doc
    // placeholder generically (the first mentioned prompt, or "Table"/"List").
    const firstReferencedLabel = groupPrompts.find(
      (p) => p.objectIdentifier === referencedPromptIds[0]
    )?.label;
    const referenceLabel = isTable
      ? "Table"
      : isListAs
        ? singlePrompt
          ? (selectedPrompt?.label ?? promptLabel ?? "List")
          : (firstReferencedLabel ?? "List")
        : promptLabel;
    await onSubmit(model, referenceLabel);
  };

  const showAnswerListFormat =
    !isListAs &&
    singleFormat === ESingleDocumentReferenceFormat.Answer &&
    promptDataType === PromptDataType.ListOfValues;

  return (
    <Stack gap={1}>
      {/* Prompt selection — group first, then prompt */}
      {isEditMode ? (
        <Box
          border="1px solid"
          borderColor="primary.main"
          borderRadius={1}
          p={2}
          sx={{ backgroundColor: "primary.light" }}
        >
          <Typography variant="body2" color="primary">
            {promptLabel || "Reference"}
          </Typography>
          {promptDataType !== undefined && (
            <Typography variant="caption" color="textSecondary">
              Prompt type: {GetFilterDataTypeString(promptDataType)}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {!lockedGroupObjectIdentifier && (
            <ControlledAutocomplete<GroupModel>
              value={selectedGroup}
              onChange={(group) => {
                setSelectedGroup(group);
                setSelectedPrompt(null);
                setPromptSearch("");
              }}
              inputValue={groupSearch}
              onInputChange={setGroupSearch}
              options={groups}
              loading={groupsLoading}
              getOptionLabel={(option) => option?.name ?? ""}
              label="Select group"
              clearable
            />
          )}
        </>
      )}

      {/* List As format */}
      {isListAs && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <FormLabel sx={{ fontSize: "0.9rem", pb: 1 }}>Reference Format *</FormLabel>
          <ButtonGroup
            buttons={[
              { id: EMultiDocumentListFormat.BulletedList, label: "Bullet list" },
              { id: EMultiDocumentListFormat.NumberedList, label: "Numbered list" },
              { id: EMultiDocumentListFormat.Paragraph, label: "Paragraphs" },
              { id: EMultiDocumentListFormat.Table, label: "Table" },
            ]}
            defaultSelected={multiFormat}
            onSelectionChange={(id) => setMultiFormat(id as EMultiDocumentListFormat)}
          />
        </FormControl>
      )}

      {/* Per-document format */}
      {!isListAs && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <FormLabel sx={{ fontSize: "0.9rem", pb: 1 }}>Reference Format *</FormLabel>
          <ButtonGroup
            buttons={[
              { id: ESingleDocumentReferenceFormat.Document, label: "Document" },
              { id: ESingleDocumentReferenceFormat.Quote, label: "Quote" },
              { id: ESingleDocumentReferenceFormat.Answer, label: "Answer" },
            ]}
            defaultSelected={singleFormat}
            onSelectionChange={(id) => setSingleFormat(id as ESingleDocumentReferenceFormat)}
          />
        </FormControl>
      )}

      {/* Per-document prompt selection — a single-document reference targets one
          prompt, shown once a format is chosen. (List As references pick many
          prompts below.) */}
      {!isEditMode && !isListAs && !!singleFormat && (
        <Box sx={{ mt: 2 }}>
          <ControlledAutocomplete<PromptModel>
            value={selectedPrompt}
            onChange={setSelectedPrompt}
            inputValue={promptSearch}
            onInputChange={setPromptSearch}
            options={prompts}
            loading={promptsLoading}
            getOptionLabel={(option) => option?.label ?? ""}
            label="Select prompt"
            disabled={!effectiveGroupObjectIdentifier}
            clearable
          />
          {submitted && !selectedPrompt && (
            <Typography variant="caption" color="error">
              Select a prompt.
            </Typography>
          )}
          {selectedPrompt && (
            <Box
              border="1px solid"
              borderColor="primary.main"
              borderRadius={1}
              p={2}
              mt={1}
              sx={{ backgroundColor: "primary.light" }}
            >
              <Typography variant="body2" color="primary">
                {selectedPrompt.label}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {selectedPrompt.description}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Prompt type: {GetFilterDataTypeString(selectedPrompt.promptDataTypeId)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showAnswerListFormat && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <FormLabel sx={{ fontSize: "0.9rem", pb: 1 }}>Answer List As Format *</FormLabel>
          <ButtonGroup
            buttons={[
              { id: ESingleDocumentListFormat.BulletedList, label: "Bulleted List" },
              { id: ESingleDocumentListFormat.NumberedList, label: "Numbered List" },
              { id: ESingleDocumentListFormat.Text, label: "Text" },
            ]}
            defaultSelected={answerListFormat}
            onSelectionChange={(id) => setAnswerListFormat(id as ESingleDocumentListFormat)}
          />
        </FormControl>
      )}

      {/* Single-prompt (old) vs multi-prompt (new) List As authoring. Only
          offered when adding; editing opens whichever mode the reference was
          saved in. */}
      {!isEditMode && isListAs && !!multiFormat && effectiveGroupObjectIdentifier && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={singlePrompt}
                onChange={(_, checked) => setSinglePrompt(checked)}
              />
            }
            label="Single prompt reference"
          />
          <Typography variant="caption" sx={{ display: "block", color: "grey.600" }}>
            {singlePrompt
              ? "Pick one prompt and use “@” to insert its answer, quote or document name."
              : "Reference several prompts inline with “@” — the reference uses whichever you mention."}
          </Typography>
          {singlePrompt && (
            <Box sx={{ mt: 1.5 }}>
              <ControlledAutocomplete<PromptModel>
                value={selectedPrompt}
                onChange={setSelectedPrompt}
                inputValue={promptSearch}
                onInputChange={setPromptSearch}
                options={prompts}
                loading={promptsLoading}
                getOptionLabel={(option) => option?.label ?? ""}
                label="Select prompt"
                disabled={!effectiveGroupObjectIdentifier}
                clearable
              />
              {submitted && !selectedPrompt && (
                <Typography variant="caption" color="error">
                  Select a prompt.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* List As templates — single-prompt uses the legacy "@" field mention;
          multi-prompt references prompts inline with "@", then "." for the
          field. The group must be chosen first so "@" has prompts to offer. */}
      {isListAs && multiFormat && effectiveGroupObjectIdentifier && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {isTable ? "Column templates" : "Template"}
          </Typography>
          <Box
            sx={{
              mb: 1.5,
              p: 1.25,
              borderRadius: 1,
              backgroundColor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            {singlePrompt ? (
              <Typography
                variant="caption"
                sx={{ display: "block", color: "grey.600", lineHeight: 1.5 }}
              >
                {isTable
                  ? "For each column, type “@” to insert the selected prompt’s answer, quote or document name."
                  : "Type “@” to insert the selected prompt’s answer, quote or document name, and mix it with your own wording."}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={{ display: "block", color: "grey.600", lineHeight: 1.5 }}
              >
                {isTable
                  ? "For each column, type “@” to pick a prompt, then “.” to choose what to pull from it"
                  : "Write your text, and type “@” wherever you want to pull in an answer — pick a prompt, then “.” to choose what to pull from it"}
                {" — for example "}
                <Box component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
                  @Agreement Date
                </Box>
                <Box component="span" sx={{ color: "primary.dark", fontWeight: 600 }}>
                  .document
                </Box>
                {". You can reference several prompts, and mix them with your own wording."}
              </Typography>
            )}
          </Box>
          {isTable ? (
            <TemplateColumnsEditor
              columns={templateColumns}
              onChange={setTemplateColumns}
              prompts={singlePrompt ? undefined : promptMentionSources}
              singlePrompt={singlePrompt}
              anchorPromptDataType={promptDataType}
            />
          ) : (
            <MentionEditor
              value={template}
              onChange={setTemplate}
              placeholder={
                singlePrompt
                  ? "Type @ to insert the answer, quote or document name…"
                  : "Type @ to insert a prompt, then . to pick its answer, document or quote…"
              }
              prompts={singlePrompt ? undefined : promptMentionSources}
              singlePrompt={singlePrompt}
              anchorPromptDataType={promptDataType}
            />
          )}
          {!isEditMode && !singlePrompt && submitted && referencedPromptIds.length === 0 && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              Reference at least one prompt using “@”.
            </Typography>
          )}
        </Box>
      )}

      {/* Conditions */}
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Conditions
        </Typography>
        <ConditionsEditor
          conditions={conditions}
          onChange={setConditions}
          ownPromptObjectIdentifier={
            (isListAs
              ? singlePrompt
                ? (selectedPrompt?.objectIdentifier ?? existingReference?.promptObjectIdentifier)
                : // Multi-prompt draws on the "@" mentions; a legacy "@document"
                  // template has none, so fall back to the reference's own prompt.
                  (referencedPromptIds[0] ?? existingReference?.promptObjectIdentifier)
              : isEditMode
                ? existingReference?.promptObjectIdentifier
                : selectedPrompt?.objectIdentifier) ?? ""
          }
          groupObjectIdentifier={effectiveGroupObjectIdentifier || undefined}
          groupPrompts={groupPrompts}
          ownPromptDataType={
            isListAs
              ? singlePrompt
                ? (selectedPrompt?.promptDataTypeId ?? promptDataType)
                : // No "@" mentions (legacy "@document" template): fall back to the
                  // reference's own prompt data type rather than reading undefined.
                  referencedPromptIds.length > 0
                  ? (groupPrompts.find((p) => p.objectIdentifier === referencedPromptIds[0])
                      ?.promptDataTypeId ?? promptDataType)
                  : promptDataType
              : isEditMode
                ? promptDataType
                : (selectedPrompt?.promptDataTypeId ?? promptDataType)
          }
        />
      </Box>

      {(error || (submitted && validationError())) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitted && validationError()
            ? validationError()
            : "Error saving reference. Please try again."}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Stack direction="row" sx={{ flex: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ mr: 2, color: "text.primary", flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ flex: 1 }}
            onClick={handleSubmit}
            endIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {isEditMode ? "Update" : "Insert"}
          </Button>
        </Stack>
        {isEditMode && onDelete && (
          <Button
            variant="outlined"
            color="error"
            disabled={loading}
            startIcon={<Delete />}
            onClick={() => void onDelete()}
          >
            Delete
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default ReferenceBuilder;
